import type { GroupMonthlyMemo } from "./utils/groupMonthlyMemo";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GripHorizontal, StickyNote, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
import {
  clampFloatingPanelPosition,
  loadFloatingPanelPosition,
  saveFloatingPanelPosition,
  type FloatingPanelPosition,
} from "@/services/floatingPanelPosition";

type MonthlyMemoPostItProps = Readonly<{
  year: number;
  month: number;
  groupId: string | null;
  memos: readonly GroupMonthlyMemo[];
  onSelectNurse: (nurseId: string | null) => void;
  layerZIndex?: number;
  onActivate?: () => void;
  onClose?: () => void;
}>;

const PANEL_GUTTER = 12;
const KEYBOARD_MOVE_DISTANCE = 24;
const DEFAULT_PANEL_POSITION: FloatingPanelPosition = {
  x: PANEL_GUTTER,
  y: 64,
};

function formatMemoUpdatedAt(value: string | null): string | null {
  if (!value) return null;

  const updatedAt = new Date(value);
  if (Number.isNaN(updatedAt.getTime())) return null;

  return new Intl.DateTimeFormat("ko-KR", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(updatedAt);
}

function getPanelStorageKey(year: number, month: number, groupId: string | null) {
  return `roster.monthly-memo-post-it.position.v2:${groupId ?? "current"}:${year}-${month}`;
}

function getWantedAdjustmentLayer() {
  return document.querySelector<HTMLElement>(
    "[data-wanted-adjustment-focus-scope]"
  );
}

export function MonthlyMemoPostIt({
  year,
  month,
  groupId,
  memos,
  onSelectNurse,
  layerZIndex = 40,
  onActivate,
  onClose,
}: MonthlyMemoPostItProps) {
  const panelId = useId();
  const storageKey = getPanelStorageKey(year, month, groupId);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const dragOffsetRef = useRef<FloatingPanelPosition | null>(null);
  const initialPosition = useRef(
    loadFloatingPanelPosition(storageKey, "local")
  );
  const hasSavedPositionRef = useRef(initialPosition.current !== null);
  const positionRef = useRef<FloatingPanelPosition>(
    initialPosition.current ?? DEFAULT_PANEL_POSITION
  );
  const [position, setPosition] = useState<FloatingPanelPosition>(
    positionRef.current
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const selectedNurseId = useRosterFocusStore(
    (state) => state.selectedNurseId
  );
  const actionLabel = isOpen ? "월간 메모 닫기" : "월간 메모 열기";
  const panelLabel = `${year}년 ${month}월 월간 메모`;
  const closePanel = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const clampPosition = useCallback((nextPosition: FloatingPanelPosition): FloatingPanelPosition => {
    const boardDialog = getWantedAdjustmentLayer();
    const panel = panelRef.current;
    if (!boardDialog) return nextPosition;

    const boardRect = boardDialog.getBoundingClientRect();
    const panelRect = panel?.getBoundingClientRect();
    const panelWidth = panelRect?.width || panel?.offsetWidth || 368;
    const panelHeight = panelRect?.height || panel?.offsetHeight || 300;

    return clampFloatingPanelPosition(
      nextPosition,
      { width: boardRect.width, height: boardRect.height },
      { width: panelWidth, height: panelHeight },
      PANEL_GUTTER
    );
  }, []);

  const movePanel = useCallback(
    (nextPosition: FloatingPanelPosition) => {
      const next = clampPosition(nextPosition);
      positionRef.current = next;
      setPosition(next);
      return next;
    },
    [clampPosition]
  );

  const persistCurrentPosition = useCallback(() => {
    saveFloatingPanelPosition(storageKey, positionRef.current, "local");
  }, [storageKey]);

  useEffect(() => {
    const savedPosition = loadFloatingPanelPosition(storageKey, "local");
    hasSavedPositionRef.current = savedPosition !== null;
    positionRef.current = savedPosition ?? DEFAULT_PANEL_POSITION;
    setPosition(positionRef.current);
    setIsOpen(false);
    setIsPositioned(false);
  }, [storageKey]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const boardDialog = getWantedAdjustmentLayer();
    if (!boardDialog) return;

    if (!hasSavedPositionRef.current) {
      const boardRect = boardDialog.getBoundingClientRect();
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      const panelRect = panelRef.current?.getBoundingClientRect();
      const panelWidth = panelRect?.width || panelRef.current?.offsetWidth || 368;

      movePanel({
        x:
          (triggerRect?.left ?? boardRect.left + PANEL_GUTTER) -
          boardRect.left +
          (triggerRect?.width ?? 0) / 2 -
          panelWidth / 2,
        y: (triggerRect?.bottom ?? boardRect.top + PANEL_GUTTER) - boardRect.top + 8,
      });
      hasSavedPositionRef.current = true;
    } else {
      movePanel(positionRef.current);
    }

    setIsPositioned(true);
  }, [isOpen, movePanel]);

  useEffect(() => {
    if (!isOpen) return;

    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      closePanel();
    };

    document.addEventListener("keydown", closeOnEscape, true);

    return () => document.removeEventListener("keydown", closeOnEscape, true);
  }, [closePanel, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const boardDialog = getWantedAdjustmentLayer();
    if (!boardDialog) return;

    const reclamp = () => movePanel(positionRef.current);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(reclamp);

    resizeObserver?.observe(boardDialog);
    if (panelRef.current) resizeObserver?.observe(panelRef.current);
    window.addEventListener("resize", reclamp);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", reclamp);
    };
  }, [isOpen, movePanel]);

  const handleDragStart = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;

    const panelRect = panelRef.current?.getBoundingClientRect();
    if (!panelRect) return;

    dragOffsetRef.current = {
      x: event.clientX - panelRect.left,
      y: event.clientY - panelRect.top,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragMove = (event: PointerEvent<HTMLButtonElement>) => {
    const boardDialog = getWantedAdjustmentLayer();
    const dragOffset = dragOffsetRef.current;
    if (!boardDialog || !dragOffset) return;

    const boardRect = boardDialog.getBoundingClientRect();
    movePanel({
      x: event.clientX - boardRect.left - dragOffset.x,
      y: event.clientY - boardRect.top - dragOffset.y,
    });
  };

  const handleDragEnd = () => {
    if (!dragOffsetRef.current) return;

    dragOffsetRef.current = null;
    persistCurrentPosition();
  };

  const handleDragKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const movementByKey: Record<string, FloatingPanelPosition> = {
      ArrowDown: { x: 0, y: KEYBOARD_MOVE_DISTANCE },
      ArrowUp: { x: 0, y: -KEYBOARD_MOVE_DISTANCE },
      ArrowLeft: { x: -KEYBOARD_MOVE_DISTANCE, y: 0 },
      ArrowRight: { x: KEYBOARD_MOVE_DISTANCE, y: 0 },
    };
    const movement = movementByKey[event.key];
    if (!movement) return;

    event.preventDefault();
    movePanel({
      x: positionRef.current.x + movement.x,
      y: positionRef.current.y + movement.y,
    });
    persistCurrentPosition();
  };

  const handleMemoSelect = (nurseId: string) => {
    const nextSelectedNurseId =
      selectedNurseId === nurseId ? null : nurseId;
    onSelectNurse(nextSelectedNurseId);
  };

  const panel = (
    <section
      ref={panelRef}
      id={panelId}
      data-monthly-memo-post-it
      aria-label={panelLabel}
      className="pointer-events-auto absolute z-40 w-[min(23rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-amber-200 bg-amber-50 text-slate-900 shadow-lg dark:border-amber-900/70 dark:bg-amber-950/95 dark:text-amber-50"
      style={{
        left: position.x,
        top: position.y,
        opacity: isPositioned ? 1 : 0,
        zIndex: layerZIndex,
      }}
      onPointerDownCapture={onActivate}
      onFocusCapture={onActivate}
    >
      <div className="flex items-center justify-between gap-2 border-b border-amber-200/80 px-3 py-2 dark:border-amber-900/70">
        <button
          type="button"
          className="-ml-1 inline-flex min-w-0 flex-1 touch-none cursor-grab select-none items-center gap-1.5 rounded px-1 py-0.5 text-left text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-sig active:cursor-grabbing"
          aria-label="월간 메모 창 이동"
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          onKeyDown={handleDragKeyDown}
        >
          <GripHorizontal aria-hidden="true" className="size-4 shrink-0 text-amber-800/70 dark:text-amber-200/70" />
          <span className="truncate">월간 메모</span>
          <span className="shrink-0 text-xs font-normal text-amber-800/80 dark:text-amber-200/80">
            {year}년 {month}월 · {memos.length}명
          </span>
        </button>
        <button
          type="button"
          className="inline-flex size-7 shrink-0 items-center justify-center rounded text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig dark:text-amber-200 dark:hover:bg-amber-900/50"
          aria-label="월간 메모 닫기"
          onClick={closePanel}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
      {memos.length === 0 ? (
        <p className="px-3 py-8 text-center text-sm text-amber-800/80 dark:text-amber-200/80">
          등록된 월간 메모가 없습니다.
        </p>
      ) : (
        <ul
          data-testid="monthly-memo-list"
          className="max-h-[min(60vh,32rem)] overscroll-contain overflow-y-auto px-3"
        >
          {memos.map((memo) => {
            const updatedAtLabel = formatMemoUpdatedAt(memo.updated_at);

            return (
              <li
                key={memo.nurse_id}
                className="min-w-0 border-b border-amber-200/80 last:border-b-0 dark:border-amber-900/70"
              >
                <button
                  type="button"
                  className={`block w-full rounded px-1 py-3 text-left transition-colors hover:bg-amber-100/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig dark:hover:bg-amber-900/35 ${
                    selectedNurseId === memo.nurse_id
                      ? "bg-amber-100/80 dark:bg-amber-900/35"
                      : ""
                  }`}
                  aria-label={`${memo.name} ${
                    selectedNurseId === memo.nurse_id
                      ? "행 강조 해제"
                      : "행으로 이동"
                  }`}
                  aria-pressed={selectedNurseId === memo.nurse_id}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleMemoSelect(memo.nurse_id);
                  }}
                >
                  <p className="truncate text-sm font-semibold">{memo.name}</p>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-5">
                    {memo.monthly_memo}
                  </p>
                  {updatedAtLabel && (
                    <p className="mt-1.5 text-[10px] text-amber-800/80 dark:text-amber-200/80">
                      {updatedAtLabel} 수정
                    </p>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const wantedAdjustmentLayer =
    typeof document === "undefined" ? null : getWantedAdjustmentLayer();

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            data-monthly-memo-post-it
            className={`relative z-10 inline-flex size-9 items-center justify-center rounded-full border border-zinc-300 bg-transparent text-zinc-700 transition-opacity hover:border-zinc-400 hover:text-zinc-900 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig focus-visible:ring-offset-1 dark:border-zinc-600 dark:text-zinc-300 dark:hover:border-zinc-500 dark:hover:text-zinc-100 ${
              isOpen ? "border-sig text-sig dark:border-sig dark:text-sig" : ""
            }`}
            aria-label={actionLabel}
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => {
              if (isOpen) {
                closePanel();
                return;
              }

              onActivate?.();
              setIsOpen(true);
            }}
          >
            <StickyNote className="size-4" aria-hidden="true" />
            {memos.length > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-rose-300 ring-2 ring-white dark:bg-rose-400 dark:ring-zinc-900"
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6}>
          월간 메모
        </TooltipContent>
      </Tooltip>
      {isOpen &&
        (wantedAdjustmentLayer
          ? createPortal(panel, wantedAdjustmentLayer)
          : panel)}
    </>
  );
}
